import { supabase } from '../supabaseClient';
import { describeError } from '../errorMessage';
import { istNetzFehler } from '../netzFehler';
import { istKalenderTag, istUnterschriftsBild, pruefeAnfrage } from './vorankAnfragePruefung';
import { VORANK_API_PFAD, VORANK_TOKEN_MUSTER } from './vorankUnterschriftTypes';
import type { VorankuendigungData } from '../../types';
import type {
    VorankFassungStatus,
    VorankPublicAntwort,
    VorankRouteAnfrage,
    VorankRouteAntwort,
    VorankUnterschrift,
} from './vorankUnterschriftTypes';

export { istUnterschriftsBild, istVorankToken, pruefeAnfrage } from './vorankAnfragePruefung';

/**
 * Die ÖFFENTLICHE Seite `#/vorankuendigung/<token>` — ohne Anmeldung
 * (docs/vorankuendigung-unterschrift-plan.md §6).
 *
 * Gelesen wird über die anon-RPC `vorank_unterschrift_public` (Nr. 186/187),
 * unterschrieben und Korrektur gemeldet NUR über die Vercel-Route
 * `VORANK_API_PFAD` — `vorank_unterschreiben` und `vorank_korrektur_melden`
 * darf ausschließlich die service_role ausführen (Muster Materialmeldung).
 *
 * RPC-Name und Parameter EXAKT wie im SQL; ein umbenannter Parameter bricht
 * die Seite still (PGRST202). Für den anonymen Besucher ist eine fehlende
 * Funktion dasselbe wie ein ungültiger Link — er bekommt keine Diagnose, die
 * Konsole schon.
 *
 * WIRFT NIE. Die Vorabprüfung `pruefeAnfrage` liegt in
 * `vorankAnfragePruefung.ts` (rein, auch von der Route importiert) und wird
 * hier nur durchgereicht.
 */

type Zeile = Record<string, unknown>;

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const strOrNull = (v: unknown): string | null => {
    if (v == null) return null;
    const s = str(v).trim();
    return s ? s : null;
};

/** Erster gesetzter Wert unter mehreren Schlüsseln — die RPC darf snake ODER camel liefern. */
const pick = (row: Zeile, ...keys: string[]): unknown => {
    for (const k of keys) if (row?.[k] !== undefined && row?.[k] !== null) return row[k];
    return undefined;
};

/** jsonb als Objekt; Zeichenketten werden geparst, ein Array liefert seine erste Zeile. */
const alsObjekt = (data: unknown): Zeile | null => {
    if (data == null) return null;
    if (Array.isArray(data)) return data.length > 0 ? alsObjekt(data[0]) : null;
    if (typeof data === 'string') {
        try {
            return alsObjekt(JSON.parse(data));
        } catch {
            return null;
        }
    }
    return typeof data === 'object' ? (data as Zeile) : null;
};

/** Der eingefrorene Inhalt: nur ein echtes Objekt (auch als JSON-Text), nie eine Liste. */
const alsInhalt = (v: unknown): VorankuendigungData | null => {
    let wert = v;
    if (typeof v === 'string') {
        try {
            wert = JSON.parse(v);
        } catch {
            return null;
        }
    }
    return wert !== null && typeof wert === 'object' && !Array.isArray(wert)
        ? (wert as VorankuendigungData)
        : null;
};

const errorCode = (e: unknown): string =>
    typeof e === 'object' && e !== null ? str((e as { code?: unknown }).code) : '';

/** Nr. 186/187 noch nicht gefahren: PostgREST kennt die Funktion nicht. */
const istFehlendeFunktion = (e: unknown): boolean => {
    const code = errorCode(e);
    const msg = typeof e === 'object' && e !== null ? str((e as { message?: unknown }).message) : '';
    return code === 'PGRST202' || code === '42883' || /could not find the function/i.test(msg);
};

const STATUS: readonly VorankFassungStatus[] = ['offen', 'unterschrieben', 'ersetzt', 'abgelaufen'];
const istStatus = (x: unknown): x is VorankFassungStatus => STATUS.includes(x as VorankFassungStatus);

const ganzzahl = (v: unknown): number => {
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
};

/**
 * Die Unterschrift aus der RPC — geprüft durchgereicht. Ohne bekannten Weg
 * oder ohne Zeitpunkt gibt es keine; ein Bild, das kein PNG im Rahmen der RPC
 * ist, wird `null` (es landete sonst ungeprüft in `<img src>` und im PDF).
 * Bild, Art und eingetragenes Datum (E6, nur ein echter Kalendertag
 * `YYYY-MM-DD`) gehören nur zum digitalen Weg.
 */
const unterschriftAus = (roh: unknown): VorankUnterschrift | null => {
    const u = alsObjekt(roh);
    if (!u) return null;
    const weg = str(u.weg);
    const am = strOrNull(pick(u, 'am', 'unterschriebenAm', 'unterschrieben_am'));
    if ((weg !== 'digital' && weg !== 'papier') || !am) return null;
    const digital = weg === 'digital';
    const bild = pick(u, 'bild');
    const art = str(u.art);
    const datum = pick(u, 'datum', 'unterschriftDatum', 'unterschrift_datum');
    return {
        weg,
        am,
        name: strOrNull(u.name),
        funktion: strOrNull(u.funktion),
        bild: digital && istUnterschriftsBild(bild) ? bild : null,
        art: digital && (art === 'gezeichnet' || art === 'bild') ? art : null,
        datum: digital && istKalenderTag(datum) ? datum : null,
    };
};

/**
 * Antwort von `vorank_unterschrift_public` → `VorankPublicAntwort`, oder
 * `null`, wenn sie nicht lesbar ist: ohne bekannten Status oder ohne Inhalt
 * gibt es nichts zu zeigen — eine leere Seite zum Unterschreiben wäre
 * schlimmer als eine Absage. Rein.
 */
export const publicAusRpc = (raw: unknown): VorankPublicAntwort | null => {
    const row = alsObjekt(raw);
    if (!row) return null;
    const status = pick(row, 'status');
    const inhalt = alsInhalt(pick(row, 'inhalt'));
    if (!istStatus(status) || !inhalt) return null;
    return {
        status,
        projektName: strOrNull(pick(row, 'projektName', 'projekt_name')),
        projektNummer: strOrNull(pick(row, 'projektNummer', 'projekt_nummer')),
        fassungVom: str(pick(row, 'fassungVom', 'fassung_vom', 'createdAt', 'created_at')),
        gueltigBis: str(pick(row, 'gueltigBis', 'gueltig_bis')),
        seiten: ganzzahl(pick(row, 'seiten')),
        inhalt,
        unterschrift: unterschriftAus(pick(row, 'unterschrift')),
        korrekturGemeldetAm: strOrNull(pick(row, 'korrekturGemeldetAm', 'korrektur_gemeldet_am', 'korrekturAm', 'korrektur_am')),
    };
};

export type VorankPublicLaden =
    | { art: 'ok'; daten: VorankPublicAntwort }
    | { art: 'ungueltig' }
    | { art: 'fehler'; text: string };

/** Der Text, wenn das Laden nicht am Link, sondern an Netz oder Server scheiterte. */
export const VORANK_PUBLIC_NETZ_TEXT = 'Keine Verbindung zum Server — bitte Netz prüfen und die Seite neu laden.';
export const VORANK_PUBLIC_SERVER_TEXT = 'Die Vorankündigung konnte gerade nicht geladen werden. Bitte später erneut versuchen.';

/**
 * Die Fassung zum Link (RPC `vorank_unterschrift_public`).
 *
 * `ungueltig`: Token in falscher Form (ohne Aufruf), unbekannter Token
 * (`null` bzw. P0002) oder die Funktion fehlt (Nr. 186/187 nicht gefahren).
 * `fehler`: Netz, Serverfehler oder eine unlesbare Antwort — mit Klartext für
 * den Bauherrn. Ein großgeschriebener Token (abgetippter Link) wird
 * kleingeschrieben: die Datenbank vergibt nur Kleinbuchstaben.
 */
export const ladeVorankPublic = async (token: string): Promise<VorankPublicLaden> => {
    const t = (token || '').trim().toLowerCase();
    if (!VORANK_TOKEN_MUSTER.test(t)) return { art: 'ungueltig' };
    try {
        const { data, error } = await supabase.rpc('vorank_unterschrift_public', { p_token: t });
        if (error) {
            if (istFehlendeFunktion(error) || errorCode(error) === 'P0002') return { art: 'ungueltig' };
            if (istNetzFehler(error)) return { art: 'fehler', text: VORANK_PUBLIC_NETZ_TEXT };
            console.warn('Vorankündigung: Fassung zum Link nicht lesbar', describeError(error));
            return { art: 'fehler', text: VORANK_PUBLIC_SERVER_TEXT };
        }
        if (data == null) return { art: 'ungueltig' };
        const daten = publicAusRpc(data);
        if (!daten) {
            console.warn('Vorankündigung: Antwort von vorank_unterschrift_public unlesbar', data);
            return { art: 'fehler', text: VORANK_PUBLIC_SERVER_TEXT };
        }
        return { art: 'ok', daten };
    } catch (e) {
        if (istNetzFehler(e)) return { art: 'fehler', text: VORANK_PUBLIC_NETZ_TEXT };
        console.warn('Vorankündigung: Fassung zum Link nicht lesbar', describeError(e));
        return { art: 'fehler', text: VORANK_PUBLIC_SERVER_TEXT };
    }
};

// ─── Absenden über die Route ────────────────────────────────────────────────

type RouteFehler = Extract<VorankRouteAntwort, { ok: false; fehler: 'eingabe' | 'ungueltig' | 'zu_viele' | 'server' }>;
const EINFACHE_FEHLER: ReadonlyArray<RouteFehler['fehler']> = ['eingabe', 'ungueltig', 'zu_viele', 'server'];
const istEinfacherFehler = (x: unknown): x is RouteFehler['fehler'] =>
    EINFACHE_FEHLER.includes(x as RouteFehler['fehler']);

const mailAus = (x: unknown): 'gesendet' | 'fehler' | 'keine' => (x === 'gesendet' || x === 'fehler' ? x : 'keine');

/** Der Satz, wenn die Route mit dem Stand der Fassung nicht einverstanden ist, ihn aber nicht nennt. */
export const VORANK_STAND_GEAENDERT_TEXT =
    'Der Stand der Vorankündigung hat sich inzwischen geändert. Bitte die Seite neu laden.';

const textOder = <T extends object>(antwort: T, text: unknown): T => {
    const t = strOrNull(text);
    return t ? { ...antwort, text: t } : antwort;
};

/**
 * Status + Rumpf der Route → `VorankRouteAntwort`. Rein.
 *
 * Maßgeblich ist der Rumpf (Vertrag: `{ ok, … }`). Ohne lesbaren Rumpf
 * (HTML-Fehlerseite von Vercel, Proxy) gilt der Statuscode:
 * 400 → eingabe · 429 → zu_viele · sonst → server mit HTTP-Code.
 *
 * Zwei Abweichungen vom nackten Statuscode, beide mit Absicht:
 *  - Ein 404 OHNE Rumpf heißt „Route fehlt" (die Route antwortet selbst
 *    immer mit JSON), nicht „Link ungültig" — der Link war beim Laden ja
 *    gültig. Daraus wird ein Serverfehler mit HTTP-Code (Muster
 *    `meldenAntwortLesen` der Materialmeldung).
 *  - Ein 409 ohne lesbaren Zustand lässt sich nicht als `fehler: 'status'`
 *    darstellen (dort ist `status` Pflicht). Daraus wird ein Serverfehler mit
 *    der Bitte, neu zu laden — die Seite zeigt dann den echten Zustand.
 * Ein 2xx ohne `ok: true` ist unvollständig: die Unterschrift kann trotzdem
 * stehen, deshalb ebenfalls „neu laden".
 */
export const routeAntwortLesen = (status: number, body: unknown): VorankRouteAntwort => {
    const o = alsObjekt(body);
    if (o?.ok === true && status >= 200 && status < 300) {
        return { ok: true, bereits: o.bereits === true, mail: mailAus(o.mail) };
    }
    if (o?.ok === false) {
        if (o.fehler === 'status') {
            const s = pick(o, 'status');
            if (istStatus(s)) return textOder({ ok: false as const, fehler: 'status' as const, status: s }, o.text);
            return { ok: false, fehler: 'server', text: VORANK_STAND_GEAENDERT_TEXT };
        }
        if (istEinfacherFehler(o.fehler)) return textOder({ ok: false as const, fehler: o.fehler }, o.text);
    }
    if (status >= 200 && status < 300) {
        return { ok: false, fehler: 'server', text: `Die Antwort des Servers war unvollständig. ${VORANK_STAND_GEAENDERT_TEXT}` };
    }
    if (status === 400) return { ok: false, fehler: 'eingabe', text: 'Die Angaben wurden nicht angenommen. Bitte prüfen und erneut absenden.' };
    if (status === 409) return { ok: false, fehler: 'server', text: VORANK_STAND_GEAENDERT_TEXT };
    if (status === 429) return { ok: false, fehler: 'zu_viele' };
    return {
        ok: false,
        fehler: 'server',
        text: `Der Server antwortet nicht wie erwartet (HTTP ${status || 'ohne Status'}). Bitte später erneut versuchen.`,
    };
};

/**
 * POST JSON an `VORANK_API_PFAD` (relativ — dieselbe Herkunft wie die Seite,
 * Muster `materialPublicService.melden`). Wirft nie.
 *
 * Vorab `pruefeAnfrage`: eine Absage kommt als `fehler: 'eingabe'` mit Text,
 * ohne Netz. Netzfehler → `{ ok: false, fehler: 'server', text }`. Der Rumpf
 * wird erst als Text gelesen: eine HTML-Fehlerseite ließe `response.json()`
 * werfen, und der Status ginge verloren.
 */
export const sendeVorankAnfrage = async (anfrage: VorankRouteAnfrage): Promise<VorankRouteAntwort> => {
    // Ohne Zukunftsprüfung: die Uhr des Geräts kann nachgehen (`datumGrenzen`);
    // grob prüft die Route mit der Uhr des Servers, genau die RPC.
    const absage = pruefeAnfrage(anfrage);
    if (absage) return { ok: false, fehler: 'eingabe', text: absage };
    let antwort: Response;
    try {
        antwort = await fetch(VORANK_API_PFAD, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(anfrage),
        });
    } catch (e) {
        return {
            ok: false,
            fehler: 'server',
            text: `Der Server ist nicht erreichbar (${describeError(e)}). Bitte Netz prüfen und erneut versuchen.`,
        };
    }
    const roh = await antwort.text().catch(() => '');
    let body: unknown = null;
    try {
        body = roh ? JSON.parse(roh) : null;
    } catch {
        body = null;
    }
    return routeAntwortLesen(antwort.status, body);
};
